import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { config } from '../../config';
import { authService } from '../../services/auth';
import { useUser } from '../../contexts/UserContext';
import { useFreeListens } from '../../contexts/FreeListenContext';
import SignInModal from '../auth/SignInModal';
import ViewLimitModal from '../common/ViewLimitModal';
import { FreemiumGateModal } from '../common/FreemiumGateModal';
import axios from 'axios';
import {
  FaPlay,
  FaPause,
  FaStop,
  FaRedoAlt,
  FaSpinner,
  FaVolumeUp,
  FaVolumeMute
} from 'react-icons/fa';
import { Icon } from '../common/Icon';

interface MeditationDisplayProps {
  title: string;
  description?: string;
  tags?: string[];
  artUrl?: string;
  audioUrl?: string;
  duration?: number;
  className?: string;
  autoLoop?: boolean;
  scriptId?: string;
}

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const BackgroundImage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'imageUrl',
})<{ imageUrl?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${props => props.imageUrl ? `url(${props.imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg, 
    rgba(0, 0, 0, 0.4) 0%, 
    rgba(0, 0, 0, 0.1) 50%, 
    rgba(0, 0, 0, 0.1) 100%
  );
`;

const ContentSection = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 24px;
`;

const Title = styled.h2`
  color: white;
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 12px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 16px 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  max-width: 600px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
`;

const Tag = styled.span`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const AudioControlsSection = styled.div`
  position: relative;
  z-index: 2;
  padding: 20px 24px;
  display: flex;
  justify-content: center;
`;

const AudioControls = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 420px;
`;

const ControlButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' 
    ? 'rgba(255, 255, 255, 0.6)' 
    : 'rgba(255, 255, 255, 0.2)'};
  border: none;
  border-radius: 50%;
  width: ${props => props.variant === 'primary' ? '48px' : '36px'};
  height: ${props => props.variant === 'primary' ? '48px' : '36px'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.variant === 'primary' ? '#333' : 'white'};
  font-size: ${props => props.variant === 'primary' ? '20px' : '20px'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.variant === 'primary' 
      ? 'rgba(255, 255, 255, 0.5)'  
      : 'rgba(255, 255, 255, 0.3)'};
    transform: scale(.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ProgressContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProgressBar = styled.div`
  position: relative;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  cursor: pointer;
  overflow: hidden;
`;

const BufferedBar = styled.div.attrs<{ $buffered: number }>(props => ({
  style: {
    width: `${props.$buffered}%`,
  },
}))<{ $buffered: number }>`
  position: absolute;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
`;

const ProgressFill = styled.div.attrs<{ $progress: number }>(props => ({
  style: {
    width: `${props.$progress}%`,
  },
}))<{ $progress: number }>`
  position: relative;
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  border-radius: 3px;
  transition: width 0.1s ease;
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`;

const VolumeContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const VolumeSliderContainer = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 15px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 16px 12px;
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  gap: 8px;
  height: 130px;
  width: 50px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid rgba(255, 255, 255, 0.15);
  }
`;

const VolumeSlider = styled.input`
  writing-mode: bt-lr; /* IE */
  writing-mode: vertical-lr; 
  direction: rtl;
  appearance: slider-vertical;
  width: 6px;
  height: 80px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.3);
  outline: none;
  cursor: pointer;
  //transform: rotate(180deg);

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4CAF50, #8BC34A);
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4CAF50, #8BC34A);
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const VolumeText = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  text-align: center;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const MeditationDisplay: React.FC<MeditationDisplayProps> = ({
  title,
  description,
  tags = [],
  artUrl,
  audioUrl,
  duration = 0,
  className,
  autoLoop = false,
  scriptId
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [bufferedPercentage, setBufferedPercentage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const isLoopingRef = useRef(false);
  
  // Volume control state
  const [volume, setVolume] = useState(1); // 0-1 range
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);
  
  // Tracking refs (use refs to avoid re-renders that could affect audio)
  const hasTrackedListenRef = useRef(false);
  
  // User context and auth modals
  const { user, isLoggedIn } = useUser();
  const { canListen, recordListen } = useFreeListens();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showViewLimitModal, setShowViewLimitModal] = useState(false);
  const [showFreemiumGate, setShowFreemiumGate] = useState(false);
  const [viewLimitResetDate, setViewLimitResetDate] = useState<string>();
  const [hasViewLimit, setHasViewLimit] = useState(false);
  const hasTrackedCompletionRef = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    isLoopingRef.current = isLooping;
  }, [isLooping]);

  // Sync volume with audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Click outside handler to hide volume slider
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showVolumeSlider && !(event.target as Element)?.closest('[data-volume-control]')) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVolumeSlider]);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isIOS, setIsIOS] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Build S3 URL from art key
  let backgroundImageUrl = '';
  if (artUrl) {
    // artUrl is actually the S3 key (e.g., "art_xxx_xxx.webp")
    // The Lambda resize function should have created a resized version
    // Check if it already has a path (legacy data might have paths)
    
      backgroundImageUrl = `https://${config.awsS3Art}.s3.${config.awsRegion}.amazonaws.com/${artUrl}`;
    
  }
  
  // Format time in MM:SS format
  const formatTime = (seconds: number): string => {
    if (!seconds || !isFinite(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Generate optimized artwork for MediaSession (512x512)
  const generateOptimizedArtwork = useCallback(async (imageUrl: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 512;
          canvas.height = 512;
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            resolve(null);
            return;
          }
          
          // Calculate crop dimensions to maintain aspect ratio
          const aspectRatio = img.width / img.height;
          let sourceWidth = img.width;
          let sourceHeight = img.height;
          let sourceX = 0;
          let sourceY = 0;
          
          if (aspectRatio > 1) {
            // Landscape - crop width
            sourceWidth = img.height;
            sourceX = (img.width - sourceWidth) / 2;
          } else if (aspectRatio < 1) {
            // Portrait - crop height
            sourceHeight = img.width;
            sourceY = (img.height - sourceHeight) / 2;
          }
          
          // Draw cropped and scaled image
          ctx.drawImage(
            img,
            sourceX, sourceY, sourceWidth, sourceHeight,
            0, 0, 512, 512
          );
          
          // Convert to blob URL
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              resolve(url);
            } else {
              resolve(null);
            }
          }, 'image/jpeg', 0.9);
        } catch (error) {
          console.error('Error generating optimized artwork:', error);
          resolve(null);
        }
      };
      
      img.onerror = () => {
        console.error('Failed to load artwork image:', imageUrl);
        resolve(null);
      };
      
      img.src = imageUrl;
    });
  }, []);

  // Setup MediaSession API
  const setupMediaSession = useCallback(async () => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    try {
      // Generate optimized artwork
      const optimizedArtworkUrl = backgroundImageUrl ? 
        await generateOptimizedArtwork(backgroundImageUrl) : null;

      navigator.mediaSession.metadata = new MediaMetadata({
        title: title,
        artist: 'YouMeditation',
        album: 'Guided Meditation',
        artwork: optimizedArtworkUrl ? [
          { src: optimizedArtworkUrl, sizes: '512x512', type: 'image/jpeg' }
        ] : []
      });

      // Set up action handlers
      navigator.mediaSession.setActionHandler('play', () => {
        handlePlay();
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        handlePause();
      });

      navigator.mediaSession.setActionHandler('seekbackward', () => {
        if (audioRef.current) {
          audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
        }
      });

      navigator.mediaSession.setActionHandler('seekforward', () => {
        if (audioRef.current) {
          audioRef.current.currentTime = Math.min(
            audioRef.current.duration || 0,
            audioRef.current.currentTime + 10
          );
        }
      });

      navigator.mediaSession.setActionHandler('seekto', (details) => {
        if (audioRef.current && details.seekTime !== undefined) {
          audioRef.current.currentTime = details.seekTime;
        }
      });
    } catch (error) {
      console.error('Error setting up MediaSession:', error);
    }
  }, [title, backgroundImageUrl]);

  // Detect iOS
  useEffect(() => {
    const checkIOS = () => {
      // More comprehensive iOS detection
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      
      // Check for iOS devices
      if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
        return true;
      }
      
      // Also check for iPad on iOS 13+ (which reports as Mac)
      if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
        return true;
      }
      
      // Check for Safari browser (needs similar audio handling as iOS)
      if (/^((?!chrome|android).)*safari/i.test(userAgent)) {
        return true;
      }
      
      return false;
    };
    const isIOSDevice = checkIOS();
    console.log('iOS Detection:', isIOSDevice, 'UserAgent:', navigator.userAgent);
    setIsIOS(isIOSDevice);
  }, []);


  // State for S3 presigned URL
  const [s3AudioUrl, setS3AudioUrl] = useState<string | null>(null);
  const [s3UrlLoading, setS3UrlLoading] = useState(false);

  // Fetch presigned S3 URL since audioUrl is the S3 key
  useEffect(() => {
    const fetchS3Url = async () => {
      console.log('Fetching S3 URL for scriptId:', scriptId, 'audioUrl:', audioUrl, 's3AudioUrl:', s3AudioUrl, 's3UrlLoading:', s3UrlLoading, 'hasViewLimit:', hasViewLimit);
      if (!scriptId || !audioUrl || s3AudioUrl || s3UrlLoading || hasViewLimit) return;

      setS3UrlLoading(true);
      try {
        const token = localStorage.getItem('token');

        // Try authenticated endpoint if logged in
        if (isLoggedIn && token) {
          const response = await fetch(`${config.apiUrl}/scripts/${scriptId}/audio-url`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.audioUrl) {
              console.log('Fetched S3 presigned URL:', data.audioUrl);
              setS3AudioUrl(data.audioUrl);
              setHasViewLimit(false);

              // Store user tier for later use
              if (data.userTier === 1 && data.listensRemaining !== null) {
                console.log(`User is tier 1 with ${data.listensRemaining} listens remaining`);
              }
            } else if (!data.success && data.reason === 'viewLimit') {
              console.log('View limit reached for tier 1 user');
              setHasViewLimit(true);
              setViewLimitResetDate(data.nextResetDate);
              setS3AudioUrl(null);
            } else {
              console.error('Invalid response from audio-url endpoint:', data);
            }
          } else {
            console.error('Failed to fetch presigned URL:', response.status, await response.text());
          }
        } else {
          // Use anonymous endpoint for non-logged-in users
          if (!canListen(scriptId)) {
            console.log('Cannot fetch audio - free listen limit reached');
            setS3UrlLoading(false);
            return;
          }

          const response = await axios.post(
            `${config.apiUrl}/scripts/${scriptId}/anonymous-audio-url`
          );

          if (response.data.success && response.data.audioUrl) {
            console.log('Fetched anonymous S3 presigned URL:', response.data.audioUrl);
            setS3AudioUrl(response.data.audioUrl);
            setHasViewLimit(false);
          }
        }
      } catch (error: any) {
        if (error.response?.data?.error === 'LIMIT_REACHED') {
          // Anonymous user reached limit
          console.log('Anonymous user reached free listen limit');
          setShowFreemiumGate(true);
        } else {
          console.error('Error fetching S3 presigned URL:', error);
        }
      } finally {
        setS3UrlLoading(false);
      }
    };

    fetchS3Url();
  }, [scriptId, audioUrl, s3AudioUrl, s3UrlLoading, hasViewLimit, isLoggedIn]);

  // Initialize audio element
  useEffect(() => {
    if (!s3AudioUrl) return;

    // Create audio element with S3 presigned URL
    const audio = new Audio();
    // iOS requires no preload until user interaction
    audio.preload = isIOS && !hasUserInteracted ? 'none' : 'metadata';
    audio.crossOrigin = 'anonymous';
    
    audio.loop = autoLoop;
    setIsLooping(autoLoop);
    isLoopingRef.current = autoLoop;
    
    console.log('Using S3 presigned URL:', s3AudioUrl);
    
    // Don't set src on iOS until user interaction
    if (!isIOS || hasUserInteracted) {
      audio.src = s3AudioUrl;
    }
    audioRef.current = audio;
    
    // Store the S3 URL for later use on iOS
    if (isIOS && !hasUserInteracted) {
      (audio as any)._pendingUrl = s3AudioUrl;
    }

    // Event handlers
    const handleLoadStart = () => {
      // Only show loading on iOS if user has interacted
      if (!isIOS || hasUserInteracted) {
        setIsLoading(true);
        setError(null);
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setError(null);
      setRetryCount(0);
      // On iOS, ensure we've marked user interaction
      if (isIOS && !hasUserInteracted) {
        setHasUserInteracted(true);
      }
    };

    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration);
      setupMediaSession();
    };

    const handleTimeUpdate = () => {
      const currentTime = audio.currentTime;
      const duration = audio.duration;
      
      setCurrentTime(currentTime);
      updateBufferedAmount();
      
      // Track completion when 90% through (completely non-blocking)
      if (!hasTrackedCompletionRef.current && duration && currentTime > 0 && scriptId) {
        const progressPercent = (currentTime / duration) * 100;
        if (progressPercent >= 90) {
          hasTrackedCompletionRef.current = true;
          // Use setTimeout to ensure this doesn't block the timeupdate event
          setTimeout(() => {
            authService.trackMeditationCompletion(scriptId).catch(error => {
              console.error('Failed to track completion:', error);
            });
          }, 0);
        }
      }
    };

    const handleProgress = () => {

      updateBufferedAmount();
     
    };

    const handleEnded = () => {
      // if (isLooping) {
      //   audio.currentTime = 0;
      //   audio.play().catch(console.error);
      // } else {
      //   setIsPlaying(false);
      //   setCurrentTime(0);
      // }
    };

    const handleError = (e: Event) => {
      const audioError = e.target as HTMLAudioElement;
      console.error('Audio error:', e, 'Error code:', audioError.error?.code, 'Message:', audioError.error?.message);
      setIsLoading(false);
      setIsPlaying(false);
      
            
      // Retry logic for network errors
      if (retryCount < 3) {
        setError(`Loading error. Retrying... (${retryCount + 1}/3)`);
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          audio.load();
        }, 2000);
      } else {
        setError('Failed to load audio. Please check your connection.');
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      navigator.mediaSession.playbackState = 'playing';
    };

    const handlePause = () => {
      setIsPlaying(false);
      navigator.mediaSession.playbackState = 'paused';
    };

    const updateBufferedAmount = () => {
      if (audio.buffered.length > 0 && audio.duration > 0) {
        const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
        const percentage = (bufferedEnd / audio.duration) * 100;
        setBufferedPercentage(percentage);
      }
    };

    // Add event listeners
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('progress', handleProgress);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    // Update loop state
    //audio.loop = isLooping;
   
    // iOS-specific settings
    if (isIOS) {
      // Ensure playback continues in background
      audio.setAttribute('playsinline', 'true');
    }

    // Start periodic updates for smoother progress
    updateIntervalRef.current = setInterval(() => {
      if (isLoopingRef.current && audio.duration > 0 && !audio.paused) {
        // Check if we're near the end (within 10ms)
        if (audio.currentTime >= audio.duration - 0.1) {
          console.log('Manual loop triggered at:', audio.currentTime, 'duration:', audio.duration);
          // Simply reset to start - no need to call play() if already playing
          audio.currentTime = 0.00;
          setCurrentTime(audio.currentTime);
        }
      }

      if (audio && !audio.paused) {
        setCurrentTime(audio.currentTime);
      }
    }, 50); // Check very frequently for precise loop timing

    // Cleanup
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('progress', handleProgress);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, [s3AudioUrl, retryCount, setupMediaSession, isIOS, hasUserInteracted]);

  //Update loop property when state changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = isLooping;
    }
  }, [isLooping]);

  const handlePlay = useCallback(async () => {
    // Check if user is logged in first
    if (!isLoggedIn) {
      console.log('User is not logged in');
      // Check if anonymous user can listen for free
      if (!scriptId || !canListen(scriptId)) {
        setShowFreemiumGate(true);
        return;
      }
      // Record the listen for anonymous user
      if (scriptId) {
        recordListen(scriptId);
      }
    }

    // Check if user has view limit
    if (hasViewLimit) {
      console.log('User has view limit');
      setShowViewLimitModal(true);
      return;
    }
    
    if (!audioRef.current) return;

    try {
      // For iOS, handle first interaction specially
      if (isIOS && !hasUserInteracted) {
        setHasUserInteracted(true);
        setIsLoading(true); // Show loading immediately
        
        // Get the pending URL or construct it
        const pendingUrl = (audioRef.current as any)._pendingUrl;
        if (pendingUrl) {
          console.log('iOS first play - using pending URL:', pendingUrl);
          audioRef.current.src = pendingUrl;
          // Clear the pending URL
          delete (audioRef.current as any)._pendingUrl;
        } else {
          // Fallback: use S3 URL
          if (s3AudioUrl) {
            console.log('iOS first play - using S3 URL:', s3AudioUrl);
            audioRef.current.src = s3AudioUrl;
          }
        }
        
        // Force load on iOS
        audioRef.current.load();
        // Wait a bit for loading to start
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      await audioRef.current.play();
      
      // Track meditation listen on first play (non-blocking)
      if (!hasTrackedListenRef.current && scriptId) {
        hasTrackedListenRef.current = true;
        // Fire and forget - don't await this call
        authService.trackMeditationListen(scriptId).catch(error => {
          console.error('Failed to track listen:', error);
        });
        
        // Also decrement listen count for tier 1 users (non-blocking)
        if (user?.tier === 1) {
          decrementUserListen();
        }
      }
    } catch (error) {
      console.error('Play error:', error);
      setError('Failed to play audio. Tap to try again.');
      setIsLoading(false);
    }
  }, [isIOS, hasUserInteracted, s3AudioUrl, scriptId, isLoggedIn, hasViewLimit, user]);

  const handlePause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  const handlePlayPause = async () => {
    try {
      if (isPlaying) {
        if (!audioRef.current) return;
        handlePause();
      } else {
        await handlePlay();
      }
    } catch (error) {
      console.error('Playback error:', error);
    }
  };

  // Decrement user listen count for tier 1 users
  const decrementUserListen = async () => {
    try {
      const token = authService.getToken();
      if (!token) return;
      
      const response = await fetch(`${config.apiUrl}/user/decrement-listen`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Listen count updated, remaining:', data.listensRemaining);
      }
    } catch (error) {
      console.error('Failed to decrement listen count:', error);
      // Non-blocking, so we don't show an error to the user
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const handleToggleLoop = () => {
    const newLoopingState = !isLooping;
    setIsLooping(newLoopingState);
    isLoopingRef.current = newLoopingState;
    //console.log('looping toggled to:', newLoopingState);
  };

  const handleVolumeToggle = () => {
    setShowVolumeSlider(!showVolumeSlider);
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleMuteToggle = () => {
    if (isMuted) {
      // Unmute - restore previous volume
      const volumeToRestore = previousVolume > 0 ? previousVolume : 0.5;
      setVolume(volumeToRestore);
      setIsMuted(false);
      if (audioRef.current) {
        audioRef.current.volume = volumeToRestore;
      }
    } else {
      // Mute - store current volume and set to 0
      setPreviousVolume(volume);
      setVolume(0);
      setIsMuted(true);
      if (audioRef.current) {
        audioRef.current.volume = 0;
      }
    }
  };

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !audioDuration || audioDuration === 0) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * audioDuration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <>
    <Container className={className}>
      <BackgroundImage imageUrl={backgroundImageUrl} />
      <Overlay />
      
      <ContentSection>
        <Title>{title}</Title>
        {description && (
          <Description>{description}</Description>
        )}
        {tags.length > 0 && (
          <TagsContainer>
            {tags.map((tag, index) => (
              <Tag key={index}>{tag}</Tag>
            ))}
          </TagsContainer>
        )}
      </ContentSection>

      <AudioControlsSection>
        <AudioControls>
          <ControlButton
            variant="primary"
            onClick={handlePlayPause}
            disabled={isLoggedIn && !hasViewLimit && (!s3AudioUrl || s3UrlLoading || (isLoading && !isPlaying && !isIOS))}
            title={isPlaying ? "Pause" : "Play"}
          >
            {(s3UrlLoading || (isLoading && !isPlaying && (!isIOS || hasUserInteracted))) ? (
              <LoadingSpinner>
                <Icon icon={FaSpinner} size={20} />
              </LoadingSpinner>
            ) : (
              <Icon icon={isPlaying ? FaPause : FaPlay} size={20} />
            )}
          </ControlButton>

          <ControlButton
            variant="secondary"
            onClick={handleStop}
            disabled={!s3AudioUrl || s3UrlLoading}
            title="Stop"
          >
            <Icon icon={FaStop} size={16} />
          </ControlButton>

          <ControlButton
            variant="secondary"
            onClick={handleToggleLoop}
            disabled={!s3AudioUrl || s3UrlLoading}
            title={isLooping ? "Disable loop" : "Enable loop"}
            style={{ 
              backgroundColor: isLooping ? '#2196f3' : undefined,
              color: isLooping ? 'white' : undefined 
            }}
          >
            <Icon icon={FaRedoAlt} size={16} />
          </ControlButton>

          <ProgressContainer>
            <ProgressBar onClick={handleProgressClick}>
              <BufferedBar $buffered={bufferedPercentage} />
              <ProgressFill $progress={progress} />
            </ProgressBar>
            <TimeDisplay>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(audioDuration)}</span>
            </TimeDisplay>
          </ProgressContainer>

          <VolumeContainer data-volume-control>
            <VolumeSliderContainer $isVisible={showVolumeSlider}>
              <VolumeText>{Math.round(volume * 100)}%</VolumeText>
              <VolumeSlider
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              />
            </VolumeSliderContainer>
            <ControlButton
              variant="secondary"
              onClick={handleVolumeToggle}
              disabled={!s3AudioUrl || s3UrlLoading}
              title={isMuted ? "Unmute" : "Mute"}
            >
              <Icon icon={isMuted ? FaVolumeMute : FaVolumeUp} size={16} />
            </ControlButton>
          </VolumeContainer>
        </AudioControls>
        
        {error && (
          <div style={{ 
            position: 'absolute', 
            bottom: '-20px', 
            left: '50%', 
            transform: 'translateX(-50%)',
            color: '#ff6b6b',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            maxWidth: '300px'
          }}>
            {error}
          </div>
        )}
      </AudioControlsSection>
    </Container>
    
    {/* Sign In Modal */}
    <SignInModal 
      isOpen={showSignInModal} 
      onClose={() => setShowSignInModal(false)}
      onSuccess={() => {
        setShowSignInModal(false);
        // Give UserContext time to update, then reset state to trigger S3 URL fetch
        setTimeout(() => {
          //console.log('Resetting S3 URL');
          //setS3AudioUrl(null);
          //setS3UrlLoading(false);
          //setHasViewLimit(false);
          window.location.reload();
        }, 500);
      }}
    />
    
    {/* View Limit Modal */}
    <ViewLimitModal
      isOpen={showViewLimitModal}
      onClose={() => setShowViewLimitModal(false)}
      nextResetDate={viewLimitResetDate}
    />

    {/* Freemium Gate Modal */}
    <FreemiumGateModal
      isOpen={showFreemiumGate}
      onClose={() => setShowFreemiumGate(false)}
      onSignIn={() => {
        setShowFreemiumGate(false);
        setShowSignInModal(true);
      }}
      onSignUp={() => {
        setShowFreemiumGate(false);
        setShowSignInModal(true);
      }}
    />
  </>
  );
};

export default MeditationDisplay;